import React, { FC, useMemo, useRef, useState } from 'react';
import useOffers from '~Hooks/useOffers';
import { DISCOUNT_TYPE, OFFER_TYPE } from '../../../__queries__/globalTypes';
import { useButton, useCurrency, useDispatch, useI18n, useSelector, useStaffCan } from '~Hooks';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import * as actions from '~actions';
import NumberInput, { NumpadTab } from '~ComponentsImproved/Numpad';
import styled from '@emotion/styled';
import { BlueButton, Dimmer, RedButton, WhiteButton } from '~Styles';
import { GetOffers_Company_offers } from '~Hooks/__queries__';
import ListItem, { ListItemWrapper } from './ListItem';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { Textfit } from 'react-textfit';
import useConfirm from '~Hooks/useConfirm';
import { BOOKINGS_DISCOUNT_ID } from '~Providers/BookingsProvider';
import { handyBreakpoint } from '~Styles/breakpoints';
import PromoCodeDiscount from '~Components/PromoCodeDiscount';
import { GetOffers_Company_allPromoCodes } from '~Hooks/__queries__';

export const MANUAL_DISCOUNT_ID = 'MANUAL_DISCOUNT_ID';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DiscountWhiteButton = styled(WhiteButton)<{ isActive?: boolean; lowerOpacity?: boolean }>`
  position: relative;
  z-index: ${({ isActive }) => (isActive ? 5 : 2)};
  color: ${({ isActive }) => (isActive ? 'var(--colorUIRed)' : 'var(--colorBlueGray1300)')};
`;

const ListWrapper = styled.div<{ visible: boolean }>`
  width: 270px;
  height: 0;
  position: absolute;
  right: 0;
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const List = styled.div`
  overflow: auto;
`;

const Anchor = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const Container = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 254px;
  right: 0;
  border-radius: 4px;
  background-color: var(--colorWhite);
  bottom: 4px;
  max-height: calc(100vh - 287px);
  z-index: 3;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);

  ${handyBreakpoint} {
    max-height: calc(100vh - 230px);
  }
`;

type DiscountObj = {
  rate: number;
  type: DISCOUNT_TYPE;
  /** Populated if its a pre-created discount */
  offerId?: string;
  isPersistent?: boolean;
};

interface Props {
  selectedItemIds: string[];
  onComplete?: () => void;
  selectedChequeId?: string;
  isHandyTSR?: boolean;
}

const DiscountButton: FC<Props> = ({
  selectedItemIds,
  onComplete,
  selectedChequeId,
  isHandyTSR = false,
}) => {
  const listRef = useRef(null as null | HTMLDivElement);
  const { offers, hasActivePromoCodes, titleById } = useOffers();
  const currency = useCurrency();
  const dispatch = useDispatch();
  const universe = useTableUniverse();
  const staffCan = useStaffCan();
  const { i18n } = useI18n();
  const { confirm } = useConfirm();
  const tableAPI = useTableApi();
  const cheque = universe.cheques.find((c) => c.id === selectedChequeId);
  const items = universe.items.filter((i) => i.chequeId === selectedChequeId);
  const [showManualDiscount, setShowManualDiscount] = useState<boolean>(false);
  const [discountListOpen, setDiscountListOpen] = useState<boolean>(false);

  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  const resetPersistentDiscount = async (offer?: GetOffers_Company_offers) => {
    if (
      cheque &&
      (cheque?.persistentDiscount?.id === MEMBERSHIP_DISCOUNT_ID ||
        cheque?.persistentDiscount?.id === BOOKINGS_DISCOUNT_ID)
    ) {
      const confirmCopy =
        cheque.persistentDiscount.id === MEMBERSHIP_DISCOUNT_ID
          ? i18n('TM.ConfirmRemoveMembershipDiscount')
          : i18n('TM.ConfirmRemoveBookingsDiscount');
      const proceed = await confirm(confirmCopy);
      if (!proceed) return;
    }

    tableAPI.removePersistantDiscount(selectedChequeId ?? '');

    const manualDiscountsToClear = !offer
      ? items.filter((item) => !item.discountGroupId && item.discount > 0)
      : [];
    const offerItemsToClear = items.filter(
      (item) =>
        (offer?.categories.some((c) => c.items.some((i) => i.id === item.itemId)) ||
          offer?.items.some((i) => i.id === item.itemId)) &&
        !item.discountGroupId,
    );

    // Remove discount from the items matching the offer items
    const _items = [...manualDiscountsToClear, ...offerItemsToClear].map((item) => ({
      discount: 0,
      id: item.id,
    }));

    tableAPI.setDiscounts(_items);
    tableAPI.log.setDiscounts(_items, 0, DISCOUNT_TYPE.PRICE);

    setDiscountListOpen(false);
    onComplete && onComplete();
  };

  const handleResetDiscount = async () => {
    if (
      cheque &&
      (cheque?.persistentDiscount?.id === MEMBERSHIP_DISCOUNT_ID ||
        cheque?.persistentDiscount?.id === BOOKINGS_DISCOUNT_ID) &&
      selectedItemIds.length === 0
    ) {
      const confirmCopy =
        cheque.persistentDiscount.id === MEMBERSHIP_DISCOUNT_ID
          ? i18n('TM.ConfirmRemoveMembershipDiscount')
          : i18n('TM.ConfirmRemoveBookingsDiscount');
      const proceed = await confirm(confirmCopy);
      if (!proceed) return;
    }

    const itemsToResetDiscount =
      selectedItemIds.length > 0
        ? items.filter((item) => selectedItemIds.includes(item.id))
        : items;

    const _items = itemsToResetDiscount.map((item) => ({
      discount: 0,
      id: item.id,
    }));

    // Resets the persistant discount if there is one applied and the user is resetting the discount on all items (not specific ones)
    if (selectedItemIds.length === 0) {
      tableAPI.applyDiscount(selectedChequeId ?? '', {
        rate: 0,
        type: DISCOUNT_TYPE.PRICE,
        offerId: undefined,
        selectedItemIds,
        applyPersistentDiscount: false,
      });
    }

    tableAPI.setDiscounts(_items);
    tableAPI.log.setDiscounts(_items, 0, DISCOUNT_TYPE.PRICE);

    setDiscountListOpen(false);
    onComplete && onComplete();
  };

  const handleSetDiscount = ({ rate, type, offerId, isPersistent }: DiscountObj) => {
    tableAPI.applyDiscount(selectedChequeId ?? '', {
      rate,
      type,
      offerId,
      selectedItemIds,
      applyPersistentDiscount: isPersistent ?? selectedItemIds.length === 0,
    });

    setDiscountListOpen(false);
    onComplete && onComplete();
  };

  // Fired when user submits the discount numpad
  const handleSubmitManualDiscount = (value: string, tab?: NumpadTab | null) => {
    /**
     * You can't apply a discount if there are payments on the cheque.
     */
    if (cheque && cheque.paidAmount > 0) {
      dispatch(actions.addToast(i18n('TM.CantApplyDiscountWithPayments'), true));
      return;
    }

    const val = parseFloat(value);
    handleSetDiscount({
      rate: tab?.type === 'percentage' ? val : val * 100,
      type: (tab?.key as DISCOUNT_TYPE) ?? DISCOUNT_TYPE.PRICE,
    });
    setShowManualDiscount(false);
    setDiscountListOpen(false);
    onComplete && onComplete();
  };

  // Checks if we have discounts applied to the active cheques items
  const hasAppliedDiscount = useMemo(() => {
    if (selectedItemIds.length > 0) {
      return items
        .filter((item) => selectedItemIds.includes(item.id) && item)
        .some((item) => item.discount > 0);
    } else if (cheque?.persistentDiscount) {
      return true;
    } else {
      return items.some((item) => item.discount > 0);
    }
  }, [items, selectedItemIds]);

  const clearButton = useButton({
    onClick: () => handleResetDiscount(),
  });

  const handlePresetDiscountClick = (offer: GetOffers_Company_offers) => {
    if (!staffCan.PRESET_DISCOUNT) {
      dispatch(actions.addToast(i18n('General.MissingPermission'), true));
      return;
    }

    /**
     * You can't apply a discount if there are payments on the cheque.
     */
    if (cheque && cheque.paidAmount > 0) {
      dispatch(actions.addToast(i18n('TM.CantApplyDiscountWithPayments'), true));
      return;
    }

    handleSetDiscount({
      rate: offer.discountRate,
      type: offer.discountType,
      offerId: offer.id,
    });
  };

  const handlePromoCodeProceed = (promoCode: GetOffers_Company_allPromoCodes) => {
    if (cheque && cheque.paidAmount > 0) {
      dispatch(actions.addToast(i18n('TM.CantApplyDiscountWithPayments'), true));
      return;
    }

    handleSetDiscount({
      rate: promoCode.discountRate,
      type: promoCode.discountType,
      offerId: promoCode.id,
      isPersistent: true, // Always apply promo codes as persistent
    });
  };

  const handleManualDiscountClick = () => {
    if (!staffCan.MANUAL_DISCOUNT) {
      dispatch(actions.addToast(i18n('General.MissingPermission'), true));
      return;
    }

    /**
     * You can't apply a discount if there are payments on the cheque.
     */
    if (cheque && cheque.paidAmount > 0) {
      dispatch(actions.addToast(i18n('TM.CantApplyDiscountWithPayments'), true));
      return;
    }

    setShowManualDiscount(true);
    setDiscountListOpen(false);
  };

  const predefinedOrderOffers = offers.filter(
    (offer) => offer.type === OFFER_TYPE.ORDER_DISCOUNT && offer.enabled,
  );

  const discountButton = useButton({
    onClick: () => {
      if (staffCan.PRESET_DISCOUNT && predefinedOrderOffers.length > 0) {
        setDiscountListOpen(!discountListOpen);
      } else {
        handleManualDiscountClick();
      }
    },
    disabled:
      (isHandyTSR && deviceWakeState.syncingTableEvents) ||
      (cheque ? cheque.paidAmount > 0 : false),
    disabledToast:
      isHandyTSR && deviceWakeState.syncingTableEvents
        ? i18n('Handy.Tables.LoadingEvents')
        : i18n('TOAST.Discount.CantApplyDiscountWithPayments'),
  });

  const manualDiscountButton = useButton({
    onClick: handleManualDiscountClick,
  });

  const getRateDisplay = (type: DISCOUNT_TYPE, rate: number) => {
    return type === DISCOUNT_TYPE.PRICE ? currency.format(rate) : `${rate}%`;
  };

  return (
    <Wrapper>
      <ListWrapper visible={discountListOpen}>
        {discountListOpen && (
          <Dimmer
            as="span"
            active
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDiscountListOpen(false);
            }}
          />
        )}

        <Anchor>
          <Container ref={listRef}>
            <List>
              <ListItemWrapper key="manual" fluid>
                <BlueButton fluid {...manualDiscountButton}>
                  {i18n('TM.ManualDiscount')}
                </BlueButton>
                {hasAppliedDiscount && (
                  <RedButton fluid {...clearButton}>
                    {i18n('Handy.Cheque.Discount.Clear')}
                  </RedButton>
                )}
              </ListItemWrapper>

              {hasActivePromoCodes && (
                <ListItemWrapper key="promo-code" fluid>
                  <PromoCodeDiscount
                    onClick={() => setDiscountListOpen(false)}
                    onProceed={handlePromoCodeProceed}
                  />
                </ListItemWrapper>
              )}

              {predefinedOrderOffers.map((offer) => {
                const isActive =
                  cheque?.persistentDiscount?.id === offer.id && !selectedItemIds.length;

                return (
                  <ListItem
                    key={offer.id}
                    name={offer.name}
                    tagText={getRateDisplay(offer.discountType, offer.discountRate)}
                    tagColor={
                      offer.discountType === DISCOUNT_TYPE.PRICE
                        ? 'var(--colorUIGreen)'
                        : undefined
                    }
                    onClick={() =>
                      isActive ? resetPersistentDiscount(offer) : handlePresetDiscountClick(offer)
                    }
                    isActive={isActive}
                  />
                );
              })}

              {/* Show label for non-offer persistent discounts (membership or custom) */}
              {cheque?.persistentDiscount &&
                !offers.some((o) => o.id === cheque?.persistentDiscount?.id) && (
                  <ListItem
                    key={cheque.persistentDiscount.id}
                    name={
                      cheque.persistentDiscount.id === MEMBERSHIP_DISCOUNT_ID
                        ? i18n('TM.MembershipDiscount')
                        : cheque.persistentDiscount.id === BOOKINGS_DISCOUNT_ID
                        ? i18n('TM.BookingDiscount')
                        : titleById[cheque.persistentDiscount.id] || i18n('TM.ManualDiscount')
                    }
                    tagColor={
                      cheque.persistentDiscount.type === DISCOUNT_TYPE.PRICE
                        ? 'var(--colorUIGreen)'
                        : undefined
                    }
                    tagText={getRateDisplay(
                      cheque.persistentDiscount.type,
                      cheque.persistentDiscount.rate,
                    )}
                    isActive
                    onClick={() => resetPersistentDiscount()}
                  />
                )}
            </List>
          </Container>
        </Anchor>
      </ListWrapper>

      {showManualDiscount && (
        <NumberInput
          tabs={[
            {
              title: i18n('General.Percentage'),
              type: 'percentage',
              key: DISCOUNT_TYPE.PERCENTAGE,
            },
            { title: i18n('General.Price'), type: 'freeform', key: DISCOUNT_TYPE.PRICE },
          ]}
          decimal
          onSubmit={handleSubmitManualDiscount}
          onClose={() => {
            setShowManualDiscount(false);
            setDiscountListOpen(false);
          }}
        />
      )}

      <DiscountWhiteButton
        data-testid="discount"
        {...discountButton}
        fluid
        lowerOpacity={Boolean(!discountListOpen && cheque?.persistentDiscount)}
        isActive={discountListOpen}>
        <Textfit
          min={12}
          max={14}
          className="discount-label"
          key={
            discountListOpen ? 'cancel' : cheque?.persistentDiscount ? 'discounted' : 'discount'
          }>
          {discountListOpen ? i18n('General.Cancel') : i18n('POS.Discount')}
        </Textfit>
      </DiscountWhiteButton>
    </Wrapper>
  );
};

export default DiscountButton;
